import { useCallback, useEffect, useMemo, useState } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Alert, Button, Chip, Grid, Stack, Typography, useTheme } from '@mui/material'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, CanonicalPeriod, PERIODS } from '../common'
import wildfireScorePalette, { WildfireScoreBadge } from '../common/fireRisk/utils'
import { useHistory } from 'react-router-dom'
import { useCurrentUser } from '../../../hooks/users'
import { LoadingValue } from './PortfolioLoading'
import PortfolioKpiValueMotion from './PortfolioKpiValueMotion'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'
import { useLocations } from '../../../hooks/locations'

function compactNumber(n: number) {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)
}
function fixed1(n: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n)
}

function pickY(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  const node = entry.data?.data?.[tierKey]?.[period]
  const y = node?.y
  const n = typeof y === 'number' ? y : typeof y === 'string' ? parseFloat(y) : NaN
  return Number.isFinite(n) ? n : null
}

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

export default function PortfolioWildfire({
  emissionTier,
  selectedPeriod,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ groupId: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const { data: scoresByProperty, isLoading: scoreLoading } = usePortfolioClimateData('wf:score', {
    groupId: groupId ?? undefined,
  })
  const { data: aalByProperty, isLoading: aalLoading } = usePortfolioClimateData('wf:damage_loss', {
    groupId: groupId ?? undefined,
  })
  const { data: biByProperty, isLoading: biLoading } = usePortfolioClimateData('wf:bi_days', {
    groupId: groupId ?? undefined,
  })
  const { currencySymbol } = useCurrentUser()

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'wf', emissionTier)),
    [emissionTier, history],
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [])

  const tierKey = emissionTier as unknown as string // 'low' | 'moderate' | 'high'

  const scoreMetric = useMemo(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (scoresByProperty && typeof scoresByProperty === 'object') {
      Object.values(scoresByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(p => (per[p] = pickY(entry, tierKey, p)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [scoresByProperty, tierKey])

  const aalMetric = useMemo(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (aalByProperty && typeof aalByProperty === 'object') {
      Object.values(aalByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(p => (per[p] = pickY(entry, tierKey, p)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [aalByProperty, tierKey])

  const biMetric = useMemo(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (biByProperty && typeof biByProperty === 'object') {
      Object.values(biByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(p => (per[p] = pickY(entry, tierKey, p)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [biByProperty, tierKey])

  // KPIs per period
  const kpis = useMemo(() => {
    type Agg = {
      avgScore: number | null
      totalAAL: number | null
      avgBI: number | null
      highCount: number // score >= 4
    }
    const out: Record<CanonicalPeriod, Agg> = {
      [CanonicalPeriod.SHORT_TERM]: { avgScore: null, totalAAL: null, avgBI: null, highCount: 0 },
      [CanonicalPeriod.MID_TERM]: { avgScore: null, totalAAL: null, avgBI: null, highCount: 0 },
      [CanonicalPeriod.LONG_TERM]: { avgScore: null, totalAAL: null, avgBI: null, highCount: 0 },
    }

    PERIODS.forEach(p => {
      const collect = (metricValues: Record<number, Partial<Record<CanonicalPeriod, number | null>>>) => {
        if (activePropertyIds) {
          return activePropertyIds
            .map(id => metricValues[id]?.[p] ?? null)
            .filter((v): v is number => typeof v === 'number')
        }
        return Object.values(metricValues)
          .map(v => v[p])
          .filter((v): v is number => typeof v === 'number')
      }
      const scoreValues = collect(scoreMetric.values)
      const AALValues = collect(aalMetric.values)
      const BIValues = collect(biMetric.values)
      const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
      const sum = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) : null)
      out[p] = {
        avgScore: mean(scoreValues),
        totalAAL: sum(AALValues),
        avgBI: mean(BIValues),
        highCount: scoreValues.filter(v => v >= 4).length,
      }
    })

    return out
  }, [activePropertyIds, scoreMetric.values, aalMetric.values, biMetric.values])

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'score',
        label: 'Risk Rating',
        perPeriod: true,
        width: 130,
        loading: scoreLoading,
        getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        renderScore: value => <WildfireScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusScore',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: scoreLoading,
          getValue: propertyId => scoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'aal',
        label: 'AAL',
        perPeriod: true,
        width: 150,
        loading: aalLoading,
        suffix: '/ year',
        getValue: (propertyId, period) => aalMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusAAL',
          label: 'Status (AAL)',
          hiddenByDefault: true,
          loading: aalLoading,
          getValue: propertyId => aalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'bi',
        label: 'Business Interruption',
        perPeriod: true,
        width: 170,
        loading: biLoading,
        format: 'fixed1',
        suffix: 'days/year',
        getValue: (propertyId, period) => biMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusBI',
          label: 'Status (Business Interruption)',
          hiddenByDefault: true,
          loading: biLoading,
          getValue: propertyId => biMetric.status[propertyId] ?? null,
        },
      },
    ],
    [
      aalLoading,
      aalMetric.status,
      aalMetric.values,
      biLoading,
      biMetric.status,
      biMetric.values,
      scoreLoading,
      scoreMetric.status,
      scoreMetric.values,
    ],
  )

  const scoreNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'score',
      label: 'Risk Rating',
      perPeriod: true,
      loading: scoreLoading,
      getValue: (propertyId, period) => scoreMetric.values[propertyId]?.[period] ?? null,
      renderValue: (value, _ctx) => <WildfireScoreBadge score={value} size="dense" />,
    }),
    [scoreLoading, scoreMetric.values],
  )

  const aalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'aal',
      label: 'AAL',
      perPeriod: true,
      loading: aalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: (propertyId, period) => aalMetric.values[propertyId]?.[period] ?? null,
    }),
    [aalLoading, aalMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: scoreNumericMetric,
        palette: wildfireScorePalette,
        labels: {
          1: 'Negligible',
          2: 'Low',
          3: 'Moderate',
          4: 'High',
          5: 'Very High',
        },
        normalize: v => Math.min(Math.max(Math.round(v), 1), 5),
        default: true,
      },
      {
        key: 'aal',
        label: 'AAL',
        type: 'continuous',
        metric: aalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
    ],
    [aalNumericMetric, scoreNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'aal',
        label: 'AAL',
        type: 'scaled',
        metric: aalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [aalNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'bi',
        label: 'Business Interruption',
        perPeriod: true,
        loading: biLoading,
        decimals: 1,
        suffix: 'days/year',
        getValue: (propertyId, period) => biMetric.values[propertyId]?.[period] ?? null,
      },
    ],
    [biLoading, biMetric.values],
  )

  const selectedKpi = kpis[selectedPeriod]
  const kpiMotionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }>
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}
        {/* KPI strip */}
        <Grid container spacing={2}>
          {/* Average Score per period */}
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard title="Average Risk Rating" transitionKey={kpiMotionKey}>
              <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                <WildfireScoreBadge score={selectedKpi.avgScore} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          {/* High / Very High counts */}
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard title="High / Very High Risk Ratings" transitionKey={kpiMotionKey}>
              <LoadingValue loading={scoreLoading} variant="rounded" width={60} height={24}>
                <Chip label={selectedKpi.highCount} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          {/* Total Average Annual Loss (AAL) per period */}
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard title="Total Average Annual Loss (AAL)" transitionKey={kpiMotionKey}>
              <LoadingValue loading={aalLoading} variant="rounded" width={140} height={24}>
                <Chip
                  label={selectedKpi.totalAAL != null ? `${currencySymbol}${compactNumber(selectedKpi.totalAAL!)} / year` : '—'}
                />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          {/* Average BI Days per period */}
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard title="Average Business Interruption" transitionKey={kpiMotionKey}>
              <LoadingValue loading={biLoading} variant="rounded" width={120} height={24}>
                <Chip label={selectedKpi.avgBI != null ? `${fixed1(selectedKpi.avgBI!)} days/year` : '—'} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="score"
          defaultRadiusKey="structure_value"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        {/* Single table with all three metrics per property */}
        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'score', period: selectedPeriod, sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}
